import { Dispatch, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useField } from 'react-final-form';

import { KnmSubjectTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmSubjectTypeCodesEnum';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmSubjectTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSubjectTypeSelectOption';
import { IKnmSupervisionTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import {
  KIND_BLOCK_ID,
  SUBJECT_TYPE_BLOCK_ID,
  TYPE_BLOCK_ID,
  KNO_ORGANIZATION_BLOCK_ID,
  SUPERVISION_TYPE_BLOCK_ID,
  FEDERAL_LAW_BLOCK_ID,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { approvedPlanInspectionDecisionOption } from 'KnmPackage/MainModule/constants/inspectionDecisionOptions';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmErpSupervisionTypesSelectOptions,
  getKnmFormRef,
  getKnmPlannedActivities,
  getKnmSupervisionTypesSelectOptions,
  getCatalogKnmTypesErpSelectOptions,
  getKnmErpKindsSelectOptions,
  getknmSubjectTypesSelectOptions,
  getKnmFederalLawsSelectOptions,
  getKnmSystemType,
  getKnmDateReasonChangeKno,
  getKnmOldKnoOrganization,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { getDictionariesKnoOrganizationsSelectOptions } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IFederalLawErvk } from 'APIPackage/CatalogsModule/interfaces/IFederalLawErvk';
import { IKnmSubjectTypesERVK } from 'APIPackage/CatalogsModule/interfaces/IKnmSubjectTypesERVK';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { dictionariesFilteredFederalLawsGet } from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { KnmSystemTypesEnum } from 'KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { useKnoNameChangeText } from 'KnmPackage/MainModule/hooks/useKnmNameChangeHintText';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import styles from './KnmErpPrimaryInfo.module.scss';
import { IKnmErpFormErvkReason } from '../../KnmErpApproveInfoModule/interfaces/IKnmErpFormErvkReason';

interface IKnmErpPrimaryInfoProps {
  creationDate?: IKnm['creationDate'];
  erpId: IKnm['erpId'] | undefined;
  knmStatusCode: IKnmStatus['code'] | undefined;
  planId: IKnm['planId'] | undefined;
  subjectTypeCode: IKnmSubjectTypeSelectOption['code'] | undefined;
  supervisionType: IKnmForm['supervisionType'] | undefined;
  typeCode: IKnmTypeSelectOption['code'] | undefined;
  watchOnly?: boolean;
  settings: IAdminSetting[];
  knmCreatedDate: string | null | undefined;
}

interface IKnmErpPrimaryInfoStoreProps {
  formRef: IKnmFormRef | undefined;
  knmSupervisionTypesSelectOptions: IKnmSupervisionTypeSelectOption[] | undefined;
  knmErpSupervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  knoOrganisationsSelectOptions: IDictionaryKnoOrganizationSelectOption[] | undefined;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  federalLawsSelectOptions: IDictionarySelectOption<IFederalLawErvk>[] | undefined;
  knmSubjectTypesSelectOptions: IDictionarySelectOption<IKnmSubjectTypesERVK>[] | undefined;
  plannedActivities: boolean | undefined;
  kindsSelectOptions: IDictionarySelectOption<IKnmKind>[] | undefined;
  knmTypes294SelectOptions: IKnmTypeSelectOption[] | undefined;
  dateReasonChangeKno: string | null | undefined;
  oldKnoOrganization: string | null | undefined;
}

const KnmErpPrimaryInfoWithoutStore = ({
  creationDate,
  erpId,
  knmStatusCode,
  planId,
  subjectTypeCode,
  supervisionType,
  typeCode,
  watchOnly,
  settings,
  knmCreatedDate,
  formRef,
  knmSupervisionTypesSelectOptions,
  knmErpSupervisionTypesSelectOptions,
  knoOrganisationsSelectOptions,
  knoOrganizationsSelectOptions,
  federalLawsSelectOptions,
  plannedActivities,
  kindsSelectOptions,
  knmTypes294SelectOptions,
  knmSubjectTypesSelectOptions,
  dateReasonChangeKno,
  oldKnoOrganization,
}: IKnmErpPrimaryInfoProps & IKnmErpPrimaryInfoStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const isShowFields4123FromErvkParamTable = isLaterThanAdminSetting(
    knmCreatedDate,
    settings,
    AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
  );
  const isShowErpKind = isLaterThanAdminSetting(knmCreatedDate, settings, AdminSettingCodesEnum.ERP_KIND_VALIDATION);

  const isFieldEnabled = useMemo(() => !watchOnly && !knmStatusCode, [watchOnly, knmStatusCode]);

  const transformKnmSubjectTypesSelectOptions = useCallback(
    (arr: IKnmSubjectTypeSelectOption[] | undefined) =>
      typeCode === KnmTypeCodesEnum.KZ
        ? arr?.filter(knmSubjectTypeSelectOption => knmSubjectTypeSelectOption.code === KnmSubjectTypeCodesEnum.UL_IP)
        : arr,
    [typeCode],
  );

  const transformKnmTypesSelectOptions = useCallback(
    (arr: IKnmTypeSelectOption[] | undefined) => {
      if (subjectTypeCode && subjectTypeCode !== KnmSubjectTypeCodesEnum.UL_IP) {
        return arr?.filter(knmTypeSelectOption => knmTypeSelectOption.code !== KnmTypeCodesEnum.KZ);
      }

      if (!isShowFields4123FromErvkParamTable) return arr;

      if (plannedActivities) {
        return arr?.filter(
          knmTypeSelectOption =>
            knmTypeSelectOption.code === KnmTypeCodesEnum.PP || knmTypeSelectOption.code === KnmTypeCodesEnum.VP,
        );
      }

      return arr?.filter(knmTypeSelectOption => knmTypeSelectOption.code === KnmTypeCodesEnum.VP);
    },
    [subjectTypeCode, plannedActivities, isShowFields4123FromErvkParamTable],
  );

  const isSupervisionTypesOptionsInitialRef = useRef(true);
  const formSupervisionTypeRef = useRef(supervisionType);

  formSupervisionTypeRef.current = supervisionType;

  useEffect(() => {
    if (
      !isSupervisionTypesOptionsInitialRef.current &&
      formSupervisionTypeRef.current &&
      !knmSupervisionTypesSelectOptions?.find(option => option.id === formSupervisionTypeRef.current?.id)
    ) {
      formRef?.current.change('supervisionType', undefined);
    }
  }, [formRef, knmSupervisionTypesSelectOptions]);

  const handleKnmTypeSelectChange = useCallback(
    (value: IKnmTypeSelectOption | undefined) => {
      const isOldTypeErknm = formRef?.current.getFieldState('typeErvk')?.value?.code === KnmTypeCodesEnum.PP248P294;

      isSupervisionTypesOptionsInitialRef.current = false;
      formRef?.current.change(isShowFields4123FromErvkParamTable ? 'subjectTypeErvk' : 'subjectType', undefined);
      formRef?.current.change(isShowFields4123FromErvkParamTable ? 'reasonsErvk' : 'reasons', undefined);
      if (value?.code === KnmTypeCodesEnum.PP) {
        formRef?.current.change('inspectionDecision', approvedPlanInspectionDecisionOption);
      } else {
        formRef?.current.change('inspectionDecision');
        formRef?.current.change('checklists');
      }
      if (kindsSelectOptions && isOldTypeErknm) {
        formRef?.current.change('kindKnm');
      }
    },
    [formRef],
  );

  const handleSubjectTypeSelectChange = useCallback(() => {
    isSupervisionTypesOptionsInitialRef.current = false;
  }, []);

  const handleKnoOrganizationSelectChange = useCallback(() => {
    formRef?.current.change('collaboratingOrganizations', undefined);
    formRef?.current.change('collaboratingKnoName', undefined);
    formRef?.current.change('knoOrganizationFunctions', undefined);
    formRef?.current.change(isShowFields4123FromErvkParamTable ? 'inspectorsErvk' : 'inspectors', undefined);
  }, [formRef, isShowFields4123FromErvkParamTable]);

  const handleKnoOrganizationWithLawSelectChange = useCallback(
    (value: IDictionarySelectOption<IKnoOrganizationDictionary>) => {
      handleKnoOrganizationSelectChange();
      dispatch(dictionariesFilteredFederalLawsGet(value.value.dictVersionId));
    },
    [formRef, isShowFields4123FromErvkParamTable],
  );

  const fields4123FromErvkParamTable = () =>
    isLaterThanAdminSetting(knmCreatedDate, settings, AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM);

  const knmTypesTransformFunc = (items: IKnmTypeSelectOption[] | undefined) => {
    if (!items) return [];

    return items.filter(item => {
      if (plannedActivities) {
        return [KnmTypeCodesEnum.PP, KnmTypeCodesEnum.VP].includes(item.code as KnmTypeCodesEnum);
      }

      return item.code === KnmTypeCodesEnum.VP;
    });
  };

  const knoOrganizationErvk = useField(`knoOrganizationErvk`, { subscription: { value: true } }).input.value;
  const federalLaw = useField(`federalLaw`, { subscription: { value: true } }).input.value;
  const supervisionTypeErvk = useField(`supervisionTypeErvk`, { subscription: { value: true } }).input.value;
  const typeErvk = useField(`typeErvk`, { subscription: { value: true } }).input.value;
  const reasonsErvk = useField(`reasonsErvk`, { subscription: { value: true } }).input.value;
  const kindErvk = useField(`kindErvk`, { subscription: { value: true } }).input.value;

  const knoNameChangeText = useKnoNameChangeText(knoOrganizationErvk?.label, oldKnoOrganization, dateReasonChangeKno);

  type ToResetFields =
    | 'knoOrganizationErvk'
    | 'federalLaw'
    | 'supervisionTypeErvk'
    | 'typeErvk'
    | 'reasonsErvk'
    | 'kindErvk'
    | 'subjectTypeErvk';

  type TriggerValue =
    | IDictionarySelectOption<IKnoOrganizationDictionary>
    | IDictionarySelectOption<IFederalLawErvk>
    | IDictionarySupervisionTypeSelectOption
    | IKnmTypeSelectOption
    | IDictionarySelectOption<IKnmKind>
    | IKnmErpFormErvkReason[]
    | undefined;

  type ResetUseEffectConf = {
    triggerName: String;
    trigger: TriggerValue;
    fields: ToResetFields[];
  };

  // Конфиг затирания нижестоящих по логике заполнения полей при изменении вышестоящих
  const resetConfig: ResetUseEffectConf[] = [
    {
      triggerName: 'knoOrganizationErvk',
      trigger: knoOrganizationErvk,
      fields: ['federalLaw', 'supervisionTypeErvk', 'typeErvk', 'reasonsErvk', 'kindErvk', 'subjectTypeErvk'],
    },
    {
      triggerName: 'federalLaw',
      trigger: federalLaw,
      fields: ['supervisionTypeErvk', 'typeErvk', 'reasonsErvk', 'kindErvk', 'subjectTypeErvk'],
    },
    {
      triggerName: 'supervisionTypeErvk',
      trigger: supervisionTypeErvk,
      fields: ['typeErvk', 'reasonsErvk', 'kindErvk', 'subjectTypeErvk'],
    },
    {
      triggerName: 'typeErvk',
      trigger: typeErvk,
      fields: ['reasonsErvk', 'kindErvk', 'subjectTypeErvk'],
    },
    {
      triggerName: 'kindErvk',
      trigger: kindErvk,
      fields: ['subjectTypeErvk'],
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prevTriggersRef = useRef<Record<string, any>>({});

  useEffect(() => {
    resetConfig.forEach((obj, index) => {
      const triggerKey = `trigger_${index}`;

      prevTriggersRef.current[triggerKey] = obj.trigger;
    });
  }, []);

  resetConfig.forEach((obj, index) => {
    useEffect(() => {
      const triggerKey = `trigger_${index}`;

      const previousValue = prevTriggersRef.current[triggerKey];
      const currentValue = obj.trigger;

      if (
        fields4123FromErvkParamTable() &&
        previousValue !== undefined &&
        previousValue !== null &&
        !watchOnly &&
        !creationDate
      ) {
        if (previousValue !== currentValue && previousValue && currentValue) {
          obj.fields.forEach(field => {
            let resetValue;

            if (field === 'reasonsErvk') {
              resetValue = reasonsErvk?.length
                ? reasonsErvk?.map((reason: IKnmErpFormErvkReason[]) => ({
                    ...reason,
                    type: undefined,
                  }))
                : undefined;
            }

            formRef?.current.change(field, resetValue);
          });
        }
      }

      prevTriggersRef.current[triggerKey] = currentValue;
    }, [knoOrganizationErvk, federalLaw, supervisionTypeErvk, typeErvk, reasonsErvk, kindErvk]);
  });

  useEffect(() => {
    if (!formRef) return;
    const objectsFieldName = isShowFields4123FromErvkParamTable ? 'objectsErvk' : 'objects';

    formRef?.current.change(objectsFieldName);
    formRef?.current.change('checklists');
    formRef.current.change('objectsResults');
  }, [supervisionTypeErvk]);

  const handleKindKnmChange = useCallback(
    (e: IDictionarySelectOption<IKnmKind>) => {
      if (isShowFields4123FromErvkParamTable) {
        const ervkKind = formRef?.current.getState().values.kindErvk;

        formRef?.current.change('kindErvk', ervkKind);
        formRef?.current.change('organizations', []);
      } else {
        const objects = formRef?.current.getState().values.objectsErknm;

        if (e?.code === KnmKindCodesEnum.RO && objects && objects.length > 1) {
          formRef?.current.change('objectsErknm', [objects[0]]);
        }
        formRef?.current.change('organizations', []);
      }
    },
    [formRef],
  );

  return (
    <Section
      title={`Мероприятие по контролю ${erpId || ''}${planId ? ` / ${planId}` : ''}${
        creationDate ? ` от ${formatDate({ date: creationDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : ''
      }`}
    >
      {!fields4123FromErvkParamTable() && (
        <Row>
          <FieldBlock label="Вид КНМ" id={TYPE_BLOCK_ID} size="col-3">
            <SelectInputFieldAutoOptions
              name="type"
              id="type"
              optionName={CommonCatalogsNamesEnum.KNM_TYPES_294}
              transformFunc={transformKnmTypesSelectOptions}
              disabled={Boolean(knmStatusCode) || watchOnly}
              size="medium"
              onChange={handleKnmTypeSelectChange}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
          {isShowErpKind && (
            <FieldBlock label="Форма КНМ" id={KIND_BLOCK_ID} size="col-3">
              <SelectInputFieldAutoOptions
                name="kind"
                id="kind"
                optionName={CommonCatalogsNamesEnum.KNM_EXECUTION_TYPES}
                disabled={watchOnly}
                size="medium"
                validate={makeRequired('Поле не может быть пустым')}
              />
            </FieldBlock>
          )}
          <FieldBlock label="Тип субъекта КНМ" id={SUBJECT_TYPE_BLOCK_ID} size="col-3">
            <SelectInputFieldAutoOptions
              name="subjectType"
              id="subjectType"
              optionName={CommonCatalogsNamesEnum.KNM_SUBJECT_TYPES}
              transformFunc={transformKnmSubjectTypesSelectOptions}
              disabled={watchOnly}
              size="medium"
              onChange={handleSubjectTypeSelectChange}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
      )}
      {fields4123FromErvkParamTable() && (
        <>
          <Row>
            <FieldBlock label="Наименование органа контроля" id={KNO_ORGANIZATION_BLOCK_ID}>
              <SelectInputField
                name={fields4123FromErvkParamTable() ? 'knoOrganizationErvk' : 'knoOrganization'}
                options={fields4123FromErvkParamTable() ? knoOrganizationsSelectOptions : knoOrganisationsSelectOptions}
                isClearable
                isSearchable
                disabled={!isFieldEnabled}
                size="medium"
                onChange={value =>
                  fields4123FromErvkParamTable()
                    ? handleKnoOrganizationWithLawSelectChange(value)
                    : handleKnoOrganizationSelectChange()
                }
              />
              {knoNameChangeText && <Hint margin="top" text={knoNameChangeText} />}
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label="Федеральный закон, в соответствии с которым проводится проверка"
              id={FEDERAL_LAW_BLOCK_ID}
              placementLabel="left"
              labelSize="col-2"
            >
              <SelectInputField
                name="federalLaw"
                options={federalLawsSelectOptions}
                placeholder="Выберите федеральный закон"
                disabled={!isFieldEnabled}
                size="medium"
                className="shared-row-field"
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Вид государственного контроля (надзора)" id={SUPERVISION_TYPE_BLOCK_ID}>
              <SelectInputField
                name={'supervisionTypeErvk'}
                options={knmErpSupervisionTypesSelectOptions}
                isSearchable
                disabled={!isFieldEnabled}
                size="medium"
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Характер КНМ" id={TYPE_BLOCK_ID} size="col-3">
              <SelectInputField
                id="typeErvk"
                name="typeErvk"
                disabled={!isFieldEnabled}
                size="medium"
                onChange={handleKnmTypeSelectChange}
                options={knmTypesTransformFunc(knmTypes294SelectOptions)}
                transformFunc={knmTypesTransformFunc}
              />
            </FieldBlock>
            <FieldBlock label="Вид КНМ" id={KIND_BLOCK_ID} size="col-3">
              <SelectInputField
                id="kindErvk"
                name="kindErvk"
                options={kindsSelectOptions}
                disabled={watchOnly}
                size="medium"
                onChange={handleKindKnmChange}
              />
            </FieldBlock>
            <FieldBlock label="Тип субъекта КНМ" id={SUBJECT_TYPE_BLOCK_ID} size="col-3">
              <SelectInputField
                name="subjectTypeErvk"
                id="subjectTypeErvk"
                options={knmSubjectTypesSelectOptions}
                disabled={watchOnly}
                size="medium"
              />
            </FieldBlock>
          </Row>
        </>
      )}
      {typeCode === KnmTypeCodesEnum.KZ && (
        <Row>
          <FieldBlock
            label={
              <Tooltip
                overlayClassName={styles.FieldLabelTooltip}
                placement="top"
                content={
                  <div className={styles.FieldLabelTooltipContent}>
                    Информация, позволяющая идентифицировать способы, которыми должна проводиться контрольная закупка,
                    осуществляемая дистанционно с использованием информационно-коммуникационных технологий
                  </div>
                }
              >
                <label className="shared-field-label_row" htmlFor="testPurchaseMethod">
                  Способ проведения контрольной закупки
                </label>
              </Tooltip>
            }
            size="half"
            placementLabel="left"
          >
            <SelectInputFieldAutoOptions
              className="shared-row-field"
              name="testPurchaseMethod"
              optionName={CommonCatalogsNamesEnum.TEST_PURCHASE_METHODS}
              disabled={watchOnly}
              size="medium"
            />
          </FieldBlock>

          <FieldBlock size="half">
            <TextareaField
              className={styles.TestPurchaseDescriptionField}
              name="testPurchaseDescription"
              placeholder="Введите описание"
              disabled={watchOnly}
            />
          </FieldBlock>
        </Row>
      )}
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpPrimaryInfoStoreProps => {
  const isPm = getKnmSystemType(state) === KnmSystemTypesEnum.PM;

  return {
    formRef: getKnmFormRef(state),
    knmSupervisionTypesSelectOptions: getKnmSupervisionTypesSelectOptions(state),
    knmErpSupervisionTypesSelectOptions: getKnmErpSupervisionTypesSelectOptions(state),
    knoOrganisationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
    knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
    federalLawsSelectOptions: getKnmFederalLawsSelectOptions(state, undefined, isPm),
    knmSubjectTypesSelectOptions: getknmSubjectTypesSelectOptions(state),
    plannedActivities: getKnmPlannedActivities(state),
    knmTypes294SelectOptions: getCatalogKnmTypesErpSelectOptions(state),
    kindsSelectOptions: getKnmErpKindsSelectOptions(state),
    dateReasonChangeKno: getKnmDateReasonChangeKno(state),
    oldKnoOrganization: getKnmOldKnoOrganization(state),
  };
};

export const KnmErpPrimaryInfo = connect(mapStateToProps)(KnmErpPrimaryInfoWithoutStore);
